//src>pages>ProfilePage.js

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MAIN_API_BASE } from '../config';
import Card from "../components/card";
import Field from "../components/field";
import Modal from "../components/modal";
import Tooltip from "../components/tooltip";
import Icon from "../components/icon";
import Chart from "../components/chart";
import AssetsDonut from "../components/assetsdonut";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

/* -------- Small helpers (unchanged logic) -------- */
function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isWebkit = /WebKit/.test(ua);
  const isNotChrome = !/CriOS/.test(ua);
  return isIOS && isWebkit && isNotChrome;
}
function bustCache(url) {
  if (!url) return url;
  return url + (url.includes('?') ? '&bust=' : '?bust=') + Date.now();
}

/* ================================================== */

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [assets, setAssets] = useState([]);
  const [prices, setPrices] = useState({});
  const [totalUsd, setTotalUsd] = useState(0);
  const [loading, setLoading] = useState(true);

  const [kycStatus, setKycStatus] = useState("unverified");
  const [kycSelfie, setKycSelfie] = useState(null);
  const [kycId, setKycId] = useState(null);
  const [kycSubmitted, setKycSubmitted] = useState(false);
  const [kycSelfiePreview, setKycSelfiePreview] = useState(null);
  const [kycIdPreview, setKycIdPreview] = useState(null);
  const [kycError, setKycError] = useState("");

  const [showEditPic, setShowEditPic] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("/logo192_new.png");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarSuccess, setAvatarSuccess] = useState("");
  const [avatarError, setAvatarError] = useState("");

  const pw1 = useRef("");
  const pw2 = useRef("");
  const pwCurrent = useRef("");

  const [pwErr, setPwErr] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);
  const [accountIdCopied, setAccountIdCopied] = useState(false);

const [deferredPrompt, setDeferredPrompt] = useState(null);
const [isInstalled, setIsInstalled] = useState(false);

// Add this after your other state declarations
const saveLanguageToDatabase = async (lang) => {
  const token = localStorage.getItem("token");
  if (!token) return;
  
  try {
    await axios.post(`${MAIN_API_BASE}/profile/language`,
      { language: lang },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log('Language saved to database:', lang);
  } catch (error) {
    console.error('Failed to save language:', error);
  }
};


  /* -------- preload prices from localStorage (unchanged) -------- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("nc_prices");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") setPrices(parsed);
      }
    } catch {}
  }, []);

/* -------- PWA prompt -------- */
useEffect(() => {
  // detect already-installed (standalone) modes
  const checkInstalled = () => {
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
      || window.navigator.standalone === true; // iOS Safari
    setIsInstalled(Boolean(standalone));
  };
  checkInstalled();

  const onBIP = (e) => {
    e.preventDefault();
    window.deferredPrompt = e; 
    setDeferredPrompt(e);
  };
  const onInstalled = () => {
    setIsInstalled(true);
    setDeferredPrompt(null);
  };

  window.addEventListener('beforeinstallprompt', onBIP);
  window.addEventListener('appinstalled', onInstalled);
  window.matchMedia?.('(display-mode: standalone)')?.addEventListener?.('change', checkInstalled);

  return () => {
    window.removeEventListener('beforeinstallprompt', onBIP);
    window.removeEventListener('appinstalled', onInstalled);
    window.matchMedia?.('(display-mode: standalone)')?.removeEventListener?.('change', checkInstalled);
  };
}, []);


  /* -------- reset submit banner when not pending -------- */
  useEffect(() => {
    if (kycStatus !== "pending") setKycSubmitted(false);
  }, [kycStatus]);

  /* -------- live prices (unchanged logic) -------- */
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const res = await axios.get(`${MAIN_API_BASE}/prices`);
        if (stopped) return;
        let map = res.data?.prices;
        if (!map || !Object.keys(map).length) {
          map = {};
          (res.data?.data || []).forEach(c => {
            if (c?.symbol) map[c.symbol] = c?.quote?.USD?.price;
          });
        }
        if (map && Object.keys(map).length) {
          setPrices(prev => {
            const next = { ...prev, ...map };
            try { localStorage.setItem("nc_prices", JSON.stringify(next)); } catch {}
            return next;
          });
        }
      } catch {}
    };
    load();
    const id = setInterval(load, 10_000);
    return () => { stopped = true; clearInterval(id); };
  }, [MAIN_API_BASE]);

  /* -------- balance history (unchanged logic) -------- */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${MAIN_API_BASE}/balance/history`, { headers });
        const history = (res.data.history || []).map(row => ({
          time: row.date,
          value: Number(row.total_usd)
        }));
        setBalanceHistory(history);
      } catch {}
    })();
  }, []);

  /* -------- profile, balances, kyc (unchanged logic) -------- */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${MAIN_API_BASE}/profile`, { headers });
setUser(res.data.user);

// Load saved language from database and apply it
if (res.data.user.language && res.data.user.language !== i18n.language) {
  await i18n.changeLanguage(res.data.user.language);
  localStorage.setItem('app_language', res.data.user.language);
}

        const balRes = await axios.get(`${MAIN_API_BASE}/balance`, { headers });
        setAssets(balRes.data.assets || []);

        const kycRes = await axios.get(`${MAIN_API_BASE}/kyc/status`, { headers });
        setKycStatus((kycRes.data.status || "unverified").toLowerCase());

        setLoading(false);
      } catch {
        navigate("/login", { replace: true });
      }
    })();
    setAuthChecked(true);
  }, [navigate]);

  /* -------- compute total USD -------- */
  useEffect(() => {
    if (!assets.length) { setTotalUsd(0); return; }
    let sum = 0;
    const visibleWalletSymbols = ["USDT", "USDC", "BTC", "ETH", "BNB"];
    assets
      .filter(({ symbol }) => visibleWalletSymbols.includes(symbol))
      .forEach(({ symbol, balance }) => {
        // FIX: Treat USDC as $1 just like USDT
        const coinPrice = (symbol === "USDT" || symbol === "USDC") ? 1 : prices[symbol] || 0;
        sum += Number(balance) * coinPrice;
      });
    setTotalUsd(sum);
  }, [assets, prices]);

  /* -------- avatar bind (unchanged logic) -------- */
  useEffect(() => {
    if (!user || !user.avatar) { setAvatarUrl("/logo192_new.png"); return; }
    setAvatarUrl(user.avatar);
  }, [user]);

  /* -------- auth guard (unchanged logic) -------- */
  useEffect(() => {
    if (!authChecked) return;
    const token = localStorage.getItem("token");
    if (!token) navigate("/login", { replace: true });
  }, [authChecked, navigate]);

  /* -------- poll kyc while pending (unchanged logic) -------- */
  useEffect(() => {
    if (kycStatus !== "pending") return;
    const id = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${MAIN_API_BASE}/kyc/status`, { headers });
        setKycStatus((res.data.status || "unverified").toLowerCase());
      } catch {}
    }, 10_000);
    return () => clearInterval(id);
  }, [kycStatus]);

  /* -------- actions (unchanged logic) -------- */
  function handleLogout() {
    localStorage.removeItem("token");
    navigate("/login");
  }

  async function handleKycSubmit(e) {
    e.preventDefault();
    if (!kycSelfie || !kycId || kycStatus === "pending" || kycStatus === "approved") return;

    setKycError("");
    setKycSubmitted(true);
    try {
      setKycStatus("pending");
      const formData = new FormData();
      formData.append("selfie", kycSelfie);
      formData.append("id_card", kycId);
      const token = localStorage.getItem("token");
      await axios.post(`${MAIN_API_BASE}/kyc`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false,
      });
      setKycSelfie(null); setKycId(null);
      setKycSelfiePreview(null); setKycIdPreview(null);
    } catch (err) {
      console.error("KYC submit error:", err?.response?.status, err?.message);
      if (kycStatus !== "pending") {
        setKycError(t("upload_failed", "Upload failed. Please try again."));
        setKycSubmitted(false);
      }
    }
  }

  async function handleAvatarChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarUrl(URL.createObjectURL(file));
  }
  async function saveAvatar() {
    if (!avatarFile || !user?.id) return;
    setAvatarSuccess(""); setAvatarError("");
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("avatar", avatarFile);
      await axios.post(`${MAIN_API_BASE}/profile/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      const updated = await axios.get(`${MAIN_API_BASE}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(updated.data.user);
      setAvatarFile(null);
      setAvatarSuccess(t('profile_avatar_updated', 'Avatar updated successfully'));
      setTimeout(() => { setAvatarSuccess(""); setShowEditPic(false); }, 1700);
    } catch {
      setAvatarError(t('profile_avatar_failed', 'Failed to update avatar'));
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwErr(""); setPwSuccess("");
    if (pw1.current.value !== pw2.current.value) {
      setPwErr(t("password_unmatch", "Passwords do not match")); return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${MAIN_API_BASE}/profile/change-password`, {
        old_password: pwCurrent.current.value, new_password: pw1.current.value
      }, { headers: { Authorization: `Bearer ${token}` } });
      pwCurrent.current.value = ""; pw1.current.value = ""; pw2.current.value = "";
      setPwSuccess(t("successful", "Successful"));
      setTimeout(() => { setPwSuccess(""); setShowChangePw(false); }, 1800);
    } catch { setPwErr(t("failed", "Failed")); }
  }

/* -------- skeleton -------- */
  if (!authChecked || loading || !user) {
    // This is the updated loading screen using your video.
    return (
      <div className="fixed inset-0 w-full h-full bg-black">
        <video
          src="/loading.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  /* ======================= UI ======================= */
  const cardClass = "p-6 md:p-8 rounded-3xl bg-[#0b1020] border border-[#2c3040] shadow-md";

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center px-3 pt-8 pb-32">

      <div className="relative z-10 w-full max-w-7xl space-y-6 md:space-y-8">

        {/* ========= 1) Profile + Total Assets ========= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Profile */}
          <Card className={`md:col-span-2 ${cardClass}`}>
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                <img
                  src={avatarFile ? URL.createObjectURL(avatarFile) : bustCache(avatarUrl) || "/logo192_new.png"}
                  alt="Profile"
                  className="w-24 h-24 rounded-full border-2 border-[#2c3040] bg-[#1a2035] object-cover"
                  onError={(e) => { e.currentTarget.src = "/logo192_new.png"; }}
                />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <div className="text-2xl md:text-3xl font-black text-white tracking-tight">
                  {user.username}
                </div>

                {kycStatus === "approved" && (
                  <span className="inline-flex items-center text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                )}
              </div>

              <div
                className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase
                ${kycStatus === "approved" ? "bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20" :
                  kycStatus === "pending" ? "bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20" :
                  kycStatus === "rejected" ? "bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/20" :
                  "bg-white/5 text-gray-400 ring-1 ring-white/10"}`}
              >
                {kycStatus === "approved" ? t("verified", "Verified")
                  : kycStatus === "pending" ? t("automated_review", "Automated review")
                  : kycStatus === "rejected" ? t("needs_new_upload", "Needs new upload")
                  : t("not_verified", "Not verified")}
              </div>

              {/* Account ID */}
<div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-sky-400/20 bg-[#0b1020] px-3 py-2">
  <Icon name="id-card" className="h-4 w-4 text-sky-400" />

  <span className="text-xs text-gray-400">Account ID</span>

  <span className="font-mono text-sm font-bold text-sky-300">
    {user.id || "—"}
  </span>

  <button
    type="button"
    disabled={!user?.id}
    onClick={async () => {
      try {
        await navigator.clipboard.writeText(String(user.id));
        setAccountIdCopied(true);
        window.setTimeout(() => setAccountIdCopied(false), 1500);
      } catch {
        window.prompt("Copy your Account ID", String(user.id));
      }
    }}
    className="text-gray-400 hover:text-white"
  >
    {accountIdCopied
      ? <Icon name="check" className="h-4 w-4 text-emerald-400" />
      : <Icon name="copy" className="h-4 w-4" />}
  </button>
</div>

              <button
                className="mt-8 h-11 px-6 rounded-xl font-bold bg-rose-500/10 text-rose-400 ring-1 ring-rose-500/20 hover:bg-rose-500/20 transition flex items-center"
                onClick={handleLogout}
              >
                <Icon name="logout" className="mr-2 w-5 h-5" /> {t('profile_logout')}
              </button>
            </div>
          </Card>

          {/* Total Assets + Quick Actions */}
          <Card className={cardClass}>
            <div className="text-center">
              <div className="text-gray-400 font-medium text-sm">{t('profile_total_assets')}</div>
              <div className="mt-1 text-4xl md:text-5xl font-black text-white tracking-tight drop-shadow-md">
                {typeof totalUsd === "number"
                  ? `$${totalUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                  : "--"}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-3">
              <button 
                className="h-12 rounded-xl font-bold bg-sky-600 text-white hover:bg-sky-500 active:scale-[0.98] transition flex items-center justify-center"
                onClick={() => navigate(`/wallet?action=deposit&coin=USDT`)}
              >
                {t('profile_deposit')}
              </button>
              <button 
                className="h-12 rounded-xl font-bold bg-[#1a2343] text-gray-200 ring-1 ring-white/5 hover:bg-[#202b54] transition flex items-center justify-center"
                onClick={() => navigate(`/wallet?action=withdraw&coin=BTC`)}
              >
                <Icon name="arrow-up-right" className="mr-2" /> {t('profile_withdraw')}
              </button>
              <button 
                className="h-12 rounded-xl font-bold bg-[#1a2343] text-gray-200 ring-1 ring-white/5 hover:bg-[#202b54] transition flex items-center justify-center"
                onClick={() => navigate(`/wallet?action=convert`)}
              >
                <Icon name="swap" className="mr-2" /> {t('profile_convert')}
              </button>
            </div>
          </Card>
        </div>

        {/* ========= 2) Chart + Assets Donut ========= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <Card className={cardClass}>
            <div className="mb-4 text-gray-300 font-bold uppercase tracking-wider text-sm">{t('profile_balance_history')}</div>
            <Chart data={balanceHistory} />
          </Card>

          <Card className={cardClass}>
            <div className="mb-4 text-gray-300 font-bold uppercase tracking-wider text-sm">{t('profile_assets')}</div>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex-1 min-w-[220px]">
                <AssetsDonut assets={assets} prices={prices} plain />
              </div>
              <div className="flex-1 w-full max-w-sm space-y-1.5">
                {assets
                  .filter(a => ["USDT", "USDC", "BTC", "ETH", "BNB"].includes(a.symbol) && a.balance > 0)
                  .map((a, i) => {
                  // FIX: Treat USDC as $1 just like USDT here too
                  const price = (a.symbol === "USDT" || a.symbol === "USDC") ? 1 : prices[a.symbol] || 0;
                  const usd = Number(a.balance) * price;
                  const percent = totalUsd ? (usd / totalUsd * 100) : 0;
                  const palette = ["#ffbe0b", "#0cf574", "#38bdf8", "#f3722c", "#b5179e", "#ff006e"];
                  return (
                    <div key={a.symbol} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 hover:bg-white/10 transition border border-white/5">
                      <div className="flex items-center gap-3">
                        <span className="w-2.5 h-2.5 rounded-full shadow-lg" style={{ background: palette[i % palette.length], boxShadow: `0 0 10px ${palette[i % palette.length]}` }} />
                        <span className="w-16 font-bold text-gray-200">{a.symbol}</span>
                      </div>
                      <span className="font-bold text-gray-400">{percent.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* ========= 3) KYC + Settings ========= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          
          {/* KYC */}
          <Card className={`relative overflow-hidden ${cardClass} ${kycStatus === 'approved' ? 'border-emerald-500/20' : kycStatus === 'pending' ? 'border-amber-500/20' : ''}`}>
            {/* Ambient Background Glow */}
            <div className={`absolute -top-20 -right-20 w-48 h-48 rounded-full blur-[80px] opacity-20 pointer-events-none
              ${kycStatus === 'approved' ? 'bg-emerald-500' :
                kycStatus === 'pending' ? 'bg-amber-500' :
                kycStatus === 'rejected' ? 'bg-rose-500' : 'bg-sky-500'}`}
            ></div>

            {kycStatus === "approved" ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[260px] py-4 relative z-10">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl animate-pulse"></div>
                  <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#0b1020] to-[#121b2f] border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                    <Icon name="shield-check" className="w-10 h-10 text-emerald-400 drop-shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                    <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white shadow-lg">
                      <Icon name="check" className="w-4 h-4" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-black text-white tracking-wide mb-2">{t('Verification')}</h3>
                <div className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-widest uppercase shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2 shadow-[0_0_8px_#34d399]"></span>
                  {t("verified", "Verified")}
                </div>
                <div className="mt-6 text-[11px] text-gray-500 font-bold text-center uppercase tracking-widest">
                  {t("profile_identity_verified", "Your identity is verified.")}
                </div>
              </div>
            ) : kycStatus === "pending" ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[260px] py-4 relative z-10">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-full bg-amber-500/20 blur-xl animate-pulse"></div>
                  <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#0b1020] to-[#121b2f] border border-amber-500/30 shadow-[0_0_30px_rgba(245,158,11,0.2)]">
                    <Icon name="clock" className="w-10 h-10 text-amber-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                  </div>
                </div>
                <h3 className="text-xl font-black text-white tracking-wide mb-2">{t('Verification')}</h3>
                <div className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold tracking-widest uppercase shadow-[0_0_15px_rgba(245,158,11,0.1)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-2 shadow-[0_0_8px_#fbbf24]"></span>
                  {t("automated_review_progress", "Automated Review")}
                </div>
                <div className="mt-6 text-[11px] text-gray-500 font-bold text-center uppercase tracking-widest">
                  {t("profile_kyc_pending_message", "Review in progress. Please wait.")}
                </div>
              </div>
            ) : (
              <div className="relative z-10">
                {/* Header for Unverified / Rejected */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-white/5 gap-3">
                  <div className="flex items-center gap-3 text-white font-black text-xl">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-xl border ${kycStatus === 'rejected' ? 'bg-rose-500/10 border-rose-500/20' : 'bg-sky-500/10 border-sky-500/20'}`}>
                      <Icon name="shield" className={`w-5 h-5 ${kycStatus === 'rejected' ? 'text-rose-400' : 'text-sky-400'}`} />
                    </div>
                    {t('Verification')}
                  </div>
                  <div className={`inline-flex items-center self-start sm:self-auto text-[10px] font-bold px-3 py-1.5 rounded-lg border uppercase tracking-widest
                    ${kycStatus === 'rejected' ? 'bg-rose-500/10 border-rose-500/20 text-rose-400' : 'bg-gray-500/10 border-gray-500/20 text-gray-400'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${kycStatus === 'rejected' ? 'bg-rose-400' : 'bg-gray-400'}`}></span>
                    {kycStatus === "rejected" ? t("needs_new_upload", "Needs new upload") : t("not_verified", "Not verified")}
                  </div>
                </div>
                
                {kycStatus === "rejected" && (
                   <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-3">
                      <Icon name="alert-circle" className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-rose-200 font-medium leading-relaxed">
                         {t("profile_kyc_rejected_message", "Your previous submission was rejected. Please upload clear images.")}
                      </p>
                   </div>
                )}

                <form className="space-y-6" onSubmit={handleKycSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Selfie */}
                    <div>
                      <label className="mb-2 block font-semibold text-gray-300 text-sm">
                        <span className="inline-flex items-center gap-2">
                          <Icon name="user" className="w-4 h-4 text-sky-400" /> {t('upload_selfie', 'Upload Selfie')}
                          <Tooltip text={t('profile_tooltip_selfie', 'Please upload a clear selfie')} />
                        </span>
                      </label>
                      <div className="bg-[#0b1020]/50 border-2 border-dashed border-[#2c3040] rounded-xl px-3 py-6 flex flex-col items-center justify-center hover:border-sky-500/50 transition">
                        <input
                          type="file"
                          accept="image/*"
                          id="selfie"
                          className="hidden"
                          disabled={kycStatus === "pending" || kycStatus === "approved"}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) { setKycSelfie(file); setKycSelfiePreview(URL.createObjectURL(file)); }
                          }}
                        />
                        <label htmlFor="selfie" className="cursor-pointer flex flex-col items-center text-gray-400 hover:text-white transition w-full">
                          <Icon name="upload-cloud" className="w-8 h-8 mb-2 opacity-70" />
                          <span className="text-sm font-semibold">{t('selfie', 'Selfie')}</span>
                        </label>
                        {kycSelfiePreview && (
                          <img src={kycSelfiePreview} alt="Selfie Preview" className="rounded-lg mt-4 border border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.2)] max-w-[110px]" />
                        )}
                      </div>
                    </div>
                    {/* ID */}
                    <div>
                      <label className="mb-2 block font-semibold text-gray-300 text-sm">
                        <span className="inline-flex items-center gap-2">
                          <Icon name="id-card" className="w-4 h-4 text-sky-400" /> {t('upload_id', 'Upload ID')}
                          <Tooltip text={t('profile_tooltip_id', 'Please upload a valid government ID')} />
                        </span>
                      </label>
                      <div className="bg-[#0b1020]/50 border-2 border-dashed border-[#2c3040] rounded-xl px-3 py-6 flex flex-col items-center justify-center hover:border-sky-500/50 transition">
                        <input
                          type="file"
                          accept="image/*"
                          id="id-card"
                          className="hidden"
                          disabled={kycStatus === "pending" || kycStatus === "approved"}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) { setKycId(file); setKycIdPreview(URL.createObjectURL(file)); }
                          }}
                        />
                        <label htmlFor="id-card" className="cursor-pointer flex flex-col items-center text-gray-400 hover:text-white transition w-full">
                          <Icon name="upload-cloud" className="w-8 h-8 mb-2 opacity-70" />
                          <span className="text-sm font-semibold">{t('id', 'ID')}</span>
                        </label>
                        {kycIdPreview && (
                          <img src={kycIdPreview} alt="ID Preview" className="rounded-lg mt-4 border border-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.2)] max-w-[110px]" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center mt-2">
                    <button
                      type="submit"
                      className="h-12 px-8 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-[0_0_20px_rgba(56,189,248,0.3)] hover:scale-[1.02] transition disabled:opacity-50 disabled:pointer-events-none w-full sm:w-72 border border-white/10"
                      disabled={!kycSelfie || !kycId || kycStatus === "pending" || kycStatus === "approved"}
                    >
                      {kycStatus === "rejected" ? t("profile_reupload_review") : t("profile_submit_review")}
                    </button>
                  </div>

                  {(kycStatus === "pending" && kycSubmitted) && (
                    <div className="text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 text-center text-sm font-medium">
                      {t("profile_kyc_submitted")}
                    </div>
                  )}
                  {kycError && (
                    <div className="text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-3 text-center text-sm font-medium">
                      {kycError}
                    </div>
                  )}
                </form>

                <div className="mt-6 text-[10px] text-gray-500 text-center font-bold uppercase tracking-widest">
                  {t("profile_kyc_upload_message", "Please upload required documents")}
                </div>
              </div>
            )}
          </Card>

          {/* Settings */}
          <Card className={cardClass}>
            <div className="text-center text-white font-black text-xl mb-6">{t('Settings')}</div>

            <div className="grid grid-cols-2 gap-3">
              <button
                className="h-16 px-4 rounded-xl font-semibold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] hover:text-white transition flex flex-col items-center justify-center text-sm"
                onClick={() => setShowChangePw(true)}
              >
                <Icon name="lock" className="mb-1.5 w-5 h-5 opacity-70" /> {t('profile_change_password')}
              </button>

              <button
                className="h-16 px-4 rounded-xl font-semibold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] hover:text-white transition flex flex-col items-center justify-center text-sm"
                onClick={() => setShowEditPic(true)}
              >
                <Icon name="edit" className="mb-1.5 w-5 h-5 opacity-70" /> {t('profile_change_picture')}
              </button>

              <button
                className="h-16 px-4 rounded-xl font-semibold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] hover:text-white transition flex flex-col items-center justify-center text-sm"
                onClick={() => navigate('/about')}
              >
                <Icon name="info" className="mb-1.5 w-5 h-5 opacity-70" /> {t('about_us')}
              </button>
            </div>

            {/* Language */}
            <div className="mt-5 relative">
              <select
  className="w-full h-12 pl-4 pr-6 rounded-xl bg-[#0b1020] border border-[#2c3040] text-gray-200 font-bold focus:ring-2 focus:ring-sky-500 outline-none appearance-none"
  value={i18n.language}
  onChange={async (e) => {
  const newLang = e.target.value;
  try {
    await i18n.changeLanguage(newLang);
    await saveLanguageToDatabase(newLang);
    localStorage.setItem('app_language', newLang);
  } catch (error) {
    console.error('Language change failed:', error);
  }
}}
>
                <option value="en">English</option>
                <option value="zh">中文</option>
                <option value="es">Español</option>
              </select>
              <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-500">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>

            {/* Install Buttons */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                className="h-12 rounded-xl font-bold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] transition flex items-center justify-center"
                onClick={async () => {
  const promptEvent = window.deferredPrompt || deferredPrompt;

  if (isInstalled) { alert(t('pwa_already_installed')); return; }
  
  if (promptEvent) {
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    
    // Clear the prompts after use
    setDeferredPrompt(null);
    window.deferredPrompt = null; 
    
    if (choice?.outcome !== 'accepted') {
      alert(t('pwa_install_dismissed'));
    }
  } else {
    alert(t('pwa_install_android_instruction'));
  }
}}
              >
                <Icon name="download" className="mr-2 w-4 h-4" /> {t('profile_android_install')}
              </button>

              <button
  className="h-12 rounded-xl font-bold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
  onClick={() => {
    if (!isIOSSafari()) {
      alert(t('pwa_install_ios_instruction'));
    } else {
      navigate('/guide');
    }
  }}
  disabled={!isIOSSafari()}
>
  <Icon name="download" className="mr-2 w-4 h-4" /> {t('profile_ios_install')}
</button>
            </div>
          </Card>
        </div>

        {/* ========= 4) Referral + Support ========= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Referral */}
          <Card className={cardClass}>
            <div className="text-center text-white font-black text-xl">{t('profile_referral_code')}</div>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="px-5 py-2.5 rounded-xl bg-[#0b1020] border border-[#2c3040] text-xl font-mono text-sky-400 font-bold tracking-widest shadow-inner">
                {user.referral || "NC-INVITE-8437"}
              </div>
              <button
                className="h-12 px-6 rounded-xl font-bold bg-white/5 text-white hover:bg-white/10 ring-1 ring-white/10 transition flex items-center"
                onClick={() => { navigator.clipboard.writeText(user.referral || "NC-INVITE-8437"); alert(t('profile_copied_clipboard')); }}
              >
                <Icon name="copy" className="mr-2 w-4 h-4" /> {t('profile_copy')}
              </button>
            </div>
            <div className="mt-4 text-xs text-gray-500 font-medium text-center uppercase tracking-wider">{t('profile_referral_invite')}</div>
          </Card>

          {/* Support */}
          <Card className={cardClass}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                className="h-12 rounded-xl font-bold text-white bg-[#0088cc] hover:bg-[#0077b5] active:scale-[0.98] transition flex items-center justify-center"
                onClick={() => window.open('https://t.me/novachaindigitalsupport', '_blank')}
              >
               {t('profile_telegram')}
              </button>
            </div>
            <div className="mt-4 text-xs text-gray-500 font-medium text-center uppercase tracking-wider">
              {t('profile_support_note')}
            </div>
          </Card>
        </div>
      </div>

      {/* ========= Modals (Dark UI override) ========= */}
      <Modal 
        visible={showChangePw} 
        onClose={() => setShowChangePw(false)}
        classWrap="bg-[#0f1424] border border-[#1a2343] shadow-[0_0_40px_rgba(0,0,0,0.8)] text-white"
        classButtonClose="text-gray-400 hover:text-white"
      >
        <h3 className="text-xl font-black mb-6 text-white text-center">{t('profile_change_password')}</h3>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <Field type="password" placeholder={t('profile_current_password')} inputRef={pwCurrent} />
          <Field type="password" placeholder={t('profile_new_password')} inputRef={pw1} />
          <Field type="password" placeholder={t('profile_confirm_new_password')} inputRef={pw2} />
          
          {pwErr && <div className="text-rose-400 text-sm text-center font-medium bg-rose-500/10 py-2 rounded border border-rose-500/20">{pwErr}</div>}
          {pwSuccess && (
            <div className="text-emerald-400 text-sm text-center font-medium bg-emerald-500/10 py-2 rounded border border-emerald-500/20">
              {pwSuccess}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
            <button type="submit" className="h-12 flex-1 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-[0_0_15px_rgba(56,189,248,0.3)] hover:scale-[1.02] transition border border-white/10" disabled={!!pwSuccess}>
              {t('profile_save')}
            </button>
            <button type="button" onClick={() => setShowChangePw(false)} className="h-12 flex-1 rounded-xl font-bold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] transition">
              {t('profile_cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal 
        visible={showEditPic} 
        onClose={() => setShowEditPic(false)}
        classWrap="bg-[#0f1424] border border-[#1a2343] shadow-[0_0_40px_rgba(0,0,0,0.8)] text-white"
        classButtonClose="text-gray-400 hover:text-white"
      >
        <h3 className="text-xl font-black mb-6 text-white text-center">{t('profile_change_picture')}</h3>
        <div className="flex flex-col items-center gap-6">
          <img
            src={avatarFile ? URL.createObjectURL(avatarFile) : bustCache(avatarUrl) || "/logo192_new.png"}
            alt="Profile Preview"
            className="w-32 h-32 rounded-full border-4 border-sky-500 shadow-[0_0_20px_rgba(14,165,233,0.3)] bg-[#0b1020] object-cover"
            onError={(e) => { e.currentTarget.src = "/logo192_new.png"; }}
          />
          <input type="file" accept="image/*" id="profile-pic-input" className="hidden" onChange={handleAvatarChange} />
          
          <label htmlFor="profile-pic-input" className="w-full sm:w-2/3">
            <span className="h-12 w-full flex items-center justify-center rounded-xl font-bold bg-[#1a2343] border border-white/5 text-gray-200 hover:bg-[#202b54] hover:text-white transition cursor-pointer">
              {t('profile_choose_new_photo')}
            </span>
          </label>

          {avatarSuccess && (
            <div className="text-emerald-400 text-sm text-center font-medium bg-emerald-500/10 py-2 px-4 w-full rounded border border-emerald-500/20">
              {avatarSuccess}
            </div>
          )}
          {avatarError && (
            <div className="text-rose-400 text-sm text-center font-medium bg-rose-500/10 py-2 px-4 w-full rounded border border-rose-500/20">
              {avatarError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row w-full gap-3 mt-2">
            <button className="h-12 flex-1 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-[0_0_15px_rgba(56,189,248,0.3)] hover:scale-[1.02] transition disabled:opacity-50 border border-white/10" onClick={saveAvatar} disabled={!avatarFile}>
              {t('profile_save')}
            </button>
            <button className="h-12 flex-1 rounded-xl font-bold bg-[#1a2343] border border-white/5 text-gray-300 hover:bg-[#202b54] transition" onClick={() => setShowEditPic(false)}>
              {t('profile_cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
